import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import { EmptyState } from '@/components/EmptyState/EmptyState'
import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { useAppSelector } from '@/hooks/redux'
import { useRedirectBasedOnPermissions } from '@/hooks/useRedirectBasedOnPermissions'
import { useLazyPlanHistoryQuery } from '@/store/api/Stripe/Stripe.query'
import type { PlanHistoryItem } from '@/store/api/Stripe/Stripe.types'
import { selectItsDefaultState } from '@/store/api/Users/Users.selector'

import { HistoryPlanItem } from './HistoryPlanItem'

export const HistoryPlanComponent = () => {
  const { t } = useTranslation()
  const { hasHandleCredits } = useRedirectBasedOnPermissions()
  const isDefaultState = useAppSelector(selectItsDefaultState)
  const [currentPage, setCurrentPage] = useState(1)
  const [allRechargeHistory, setAllRechargeHistory] = useState<
    PlanHistoryItem[]
  >([])

  useEffect(() => {
    hasHandleCredits()
  }, [isDefaultState])

  const [
    makeRequest,
    {
      data: historyPlanData,
      isLoading: isLoadingPlanHistoryFirstTime,
      isFetching: isFetchingPlanHistory,
    },
  ] = useLazyPlanHistoryQuery()

  const makeBeRequest = useCallback(
    (page: number) => {
      makeRequest({ page, pageSize: 10 })
    },
    [makeRequest],
  )

  useEffect(() => {
    makeBeRequest(currentPage)
  }, [makeBeRequest, currentPage])

  useEffect(() => {
    if (historyPlanData?.rechargeHistory) {
      setAllRechargeHistory((prevHistory) => {
        if (currentPage === 1) {
          return historyPlanData.rechargeHistory
        }
        return [...prevHistory, ...historyPlanData.rechargeHistory]
      })
    }
  }, [historyPlanData, currentPage])

  const fetchMoreOnPagination = () => {
    if (!isFetchingPlanHistory && hasMoreData) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const hasMoreData = useMemo(() => {
    if (!historyPlanData) return false
    return historyPlanData.page < historyPlanData.pages
  }, [historyPlanData])

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingPlanHistory,
    hasNextPage: hasMoreData,
    onLoadMore: fetchMoreOnPagination,
    disabled: false,
    rootMargin: '0px 0px 400px 0px',
  })

  const isLoadingFirstPage =
    (isFetchingPlanHistory && currentPage === 1) ||
    isLoadingPlanHistoryFirstTime

  return (
    <div className="bg-baseColor rounded-lg p-4 justify-center">
      {!isLoadingFirstPage && allRechargeHistory.length > 0 && (
        <div>
          {allRechargeHistory.map((item: PlanHistoryItem, id) => (
            <div key={id}>
              <HistoryPlanItem data={item} />
            </div>
          ))}

          {hasMoreData && (
            <div ref={sentryRef} className="py-4 text-center">
              {isFetchingPlanHistory && (
                <p className="text-xl font-medium text-neutralColor80">
                  {t('common.loading')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {allRechargeHistory.length === 0 && !isLoadingFirstPage && (
        <div className="flex items-center justify-center h-[65vh]">
          <EmptyState text={t('dashboard.billingAndInvoices.emptyState')} />
        </div>
      )}

      {isLoadingFirstPage && (
        <div className="flex justify-center">
          <LottieSpinner />
        </div>
      )}
    </div>
  )
}
